import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import { getBreadcrumbSchema } from '@/components/feature/SchemaOrg';
import { categories, getArticlesByCategory, getCategoryBySlug } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const categoryArticles = getArticlesByCategory(slug || '');
  const otherCategories = categories.filter((c) => c.slug !== slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 whitespace-nowrap">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`龟头责${category.name} - ${category.description}`}
        description={`龟头责${category.name}相关科普文章，${category.description}。提供专业的${category.name}知识、预防方法和治疗指导。`}
        keywords={`龟头责${category.name},龟头责,男性生殖器炎症,龟头责${category.name}科普`}
        canonicalUrl={`https://www.nanke-health.com/category/${slug}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getBreadcrumbSchema([
        { name: '首页', url: 'https://www.nanke-health.com' },
        { name: category.name, url: `https://www.nanke-health.com/category/${slug}` },
      ])) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-24 pb-10 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb items={[{ name: '首页', href: '/' }, { name: category.name }]} />
            <div className="mt-6 flex items-center gap-4">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-primary-100 text-primary-600 rounded-xl">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  龟头责{category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1">
              {categoryArticles.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 text-foreground-400 rounded-full mb-4">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-600">该分类下暂无文章，敬请期待</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="w-full lg:w-64 flex-shrink-0">
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 sticky top-24">
                <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-4">
                  其他分类
                </h3>
                <div className="space-y-1">
                  {otherCategories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-50 transition-colors duration-150 whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-foreground-500`}></i>
                      <span>{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-400">{cat.articleCount}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}